// IB Flex Query XML Parser
// Parses Interactive Brokers Flex Query XML files to extract NAV data

export interface IBFlexQueryData {
  accountId: string;
  date: Date;
  startingNAV: number;
  endingNAV: number;
  mtmChange: number;
  changeInUnrealized: number;
  currency: string;
  cashPositions: Array<{
    endingCash: number;
    currency?: string;
  }>;
}

/**
 * Parse IB Flex Query XML to extract NAV data
 */
export const parseIBFlexQueryXML = (xmlString: string): IBFlexQueryData => {
  try {
    // Parse XML string
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Invalid XML format');
    }

    // Extract FlexStatement
    const flexStatement = xmlDoc.querySelector('FlexStatement');
    if (!flexStatement) {
      throw new Error('No FlexStatement found in XML');
    }

    // Extract account ID
    const accountId = flexStatement.getAttribute('accountId');
    if (!accountId) {
      throw new Error('No accountId found in FlexStatement');
    }

    // Extract date (toDate is the reporting date)
    const toDate = flexStatement.getAttribute('toDate');
    if (!toDate) {
      throw new Error('No toDate found in FlexStatement');
    }

    // Parse date (format: YYYYMMDD)
    const year = parseInt(toDate.substring(0, 4));
    const month = parseInt(toDate.substring(4, 6)) - 1; // JS months are 0-indexed
    const day = parseInt(toDate.substring(6, 8));
    const date = new Date(year, month, day);

    // Extract ChangeInNAV data
    const changeInNAV = xmlDoc.querySelector('ChangeInNAV');
    if (!changeInNAV) {
      throw new Error('No ChangeInNAV found in XML');
    }

    // Try multiple possible attributes for starting value
    const priorPeriodValue = parseFloat(changeInNAV.getAttribute('priorPeriodValue') || '0');
    const startingValueAttr = parseFloat(changeInNAV.getAttribute('startingValue') || '0');
    const transactions = parseFloat(changeInNAV.getAttribute('transactions') || '0');
    const startingValue = priorPeriodValue || startingValueAttr || transactions;
    
    const mtm = parseFloat(changeInNAV.getAttribute('mtm') || '0');
    const changeInUnrealized = parseFloat(changeInNAV.getAttribute('changeInUnrealized') || '0');
    const currency = changeInNAV.getAttribute('currency') || 'USD';

    // Try to get ending NAV from attribute first, then calculate
    const endingValueAttr = parseFloat(changeInNAV.getAttribute('endingValue') || '0');
    const endingNAV = endingValueAttr || (startingValue + mtm);

    // Extract cash positions (optional)
    const cashPositions: Array<{ endingCash: number; currency?: string }> = [];
    const cashReportCurrencies = xmlDoc.querySelectorAll('CashReportCurrency');
    cashReportCurrencies.forEach((cashCurrency) => {
      const endingCash = parseFloat(cashCurrency.getAttribute('endingCash') || '0');
      if (endingCash !== 0) {
        cashPositions.push({ endingCash });
      }
    });

    return {
      accountId,
      date,
      startingNAV: startingValue,
      endingNAV,
      mtmChange: mtm,
      changeInUnrealized,
      currency,
      cashPositions,
    };
  } catch (error: any) {
    console.error('Error parsing IB Flex Query XML:', error);
    throw new Error(`Failed to parse XML: ${error.message}`);
  }
};

/**
 * Validate that the XML contains required NAV data
 */
export const validateIBFlexQueryXML = (xmlString: string): { valid: boolean; error?: string } => {
  try {
    const data = parseIBFlexQueryXML(xmlString);
    
    // Validate required fields
    if (!data.accountId) {
      return { valid: false, error: 'Missing account ID' };
    }
    
    if (!data.date || isNaN(data.date.getTime())) {
      return { valid: false, error: 'Invalid date' };
    }
    
    if (data.endingNAV === 0 && data.startingNAV === 0) {
      return { valid: false, error: 'NAV values are zero' };
    }
    
    return { valid: true };
  } catch (error: any) {
    return { valid: false, error: error.message };
  }
};

/**
 * Format NAV data for display
 */
export const formatIBFlexQueryData = (data: IBFlexQueryData): string => {
  const changePercent = data.startingNAV !== 0 
    ? ((data.mtmChange / data.startingNAV) * 100).toFixed(2)
    : '0.00';
  
  const sign = data.mtmChange >= 0 ? '+' : '';
  
  return `
Account: ${data.accountId}
Date: ${data.date.toLocaleDateString()}
Starting NAV: ${data.currency} ${data.startingNAV.toLocaleString()}
Ending NAV: ${data.currency} ${data.endingNAV.toLocaleString()}
Change: ${sign}${data.currency} ${data.mtmChange.toLocaleString()} (${sign}${changePercent}%)
  `.trim();
};
